/**
 * CardNode - Custom node component for React Flow
 *
 * Renders a card in the canvas with:
 * - L-ID badge
 * - Title
 * - Content preview (for focused cards)
 * - Visual states (focused, hover)
 */

import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import type { CardNodeData } from './useCanvasLayout'
import './CardNode.css'

interface CardNodeProps {
  data: CardNodeData
  selected?: boolean
}

function CardNodeComponent({ data, selected }: CardNodeProps) {
  const { card, isExpanded, isFocused, displayLid } = data

  const classNames = [
    'card-node',
    isExpanded && 'card-node--expanded',
    selected && 'card-node--selected',
    isFocused && 'card-node--focused',
  ].filter(Boolean).join(' ')

  return (
    <div className={classNames}>
      {/* Left/Right handles for parent-child connections */}
      <Handle type="target" position={Position.Left} className="card-node__handle" />
      <Handle type="source" position={Position.Right} className="card-node__handle" />

      {/* Top/Bottom handles for sibling connections */}
      <Handle type="target" position={Position.Top} id="top" className="card-node__handle card-node__handle--vertical" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="card-node__handle card-node__handle--vertical" />

      <div className="card-node__header">
        <span className="card-node__lid">{displayLid}</span>
      </div>

      <div className="card-node__title">{card.title}</div>

      {card.content && (
        <div className="card-node__content">
          {isExpanded
            ? card.content.slice(0, 400)
            : card.content.slice(0, 100)}
          {(isExpanded ? card.content.length > 400 : card.content.length > 100) && '...'}
        </div>
      )}
    </div>
  )
}

export const CardNode = memo(CardNodeComponent)
